import { useQuery } from "@tanstack/react-query"
import { ArrowDownLeft, ArrowUpRight, Scale } from "lucide-react"

import { api } from "@/api/cliente"
import { monto } from "@/componentes/tesoreria/dinero"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"

import { GraficoFlujo, type MesFlujo } from "./GraficoFlujo"

type Flujo = {
  periodo: MesFlujo
  meses: MesFlujo[]
  por_banco: MesFlujo[]
  sin_convertir: string[]
}

/**
 * El flujo de caja de los bancos del 3.0 (28/10, requerimiento de Andrea, §3.1 y §3.2):
 * ingresos, egresos y neto del período, los seis meses consecutivos y el detalle por
 * banco. Sin transferencias entre cuentas propias (`services/flujo_caja.py`).
 *
 * Vivió un día en un «tablero gerencial» que se retiró por repetir el Resumen Macro y los
 * reportes: esto era lo único que no estaba en otra pantalla.
 */
export function FlujoCaja({
  filtro,
  onElegirMes,
}: {
  filtro: string
  /** Al pulsar un mes: los reportes pasan a ese mes. */
  onElegirMes: (desde: string) => void
}) {
  const consulta = useQuery({
    queryKey: ["flujo-caja", filtro],
    queryFn: () => api.get<Flujo>(`/api/v1/informes/flujo-caja?${filtro}`),
  })
  if (consulta.isPending) return <EstadoCarga etiqueta="Calculando el flujo" />
  if (consulta.isError) {
    return (
      <EstadoError
        titulo="No se pudo calcular el flujo de caja"
        error={consulta.error}
        onReintentar={() => consulta.refetch()}
      />
    )
  }
  const f = consulta.data
  const elegido = f.meses.find((m) => m.desde === f.periodo.desde && m.hasta === f.periodo.hasta)
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <TarjetaKpi
          titulo="Ingresos"
          detalle="Lo que entró a los bancos del 3.0"
          Icono={ArrowDownLeft}
          color="var(--success)"
          cantidad={`${monto(f.periodo.ingresos)} $`}
        />
        <TarjetaKpi
          titulo="Egresos"
          detalle="Lo que salió: órdenes, CxP y nómina"
          Icono={ArrowUpRight}
          color="var(--destructive)"
          cantidad={`${monto(f.periodo.egresos)} $`}
        />
        <TarjetaKpi
          titulo="Flujo neto"
          detalle="Ingresos menos egresos"
          Icono={Scale}
          color="var(--info)"
          cantidad={`${monto(f.periodo.neto)} $`}
        />
      </div>

      <div className="tarjeta space-y-3 p-4 sm:p-5">
        <h3 className="text-sm font-semibold">Los últimos seis meses</h3>
        <GraficoFlujo
          meses={f.meses}
          seleccionado={elegido?.desde ?? null}
          onElegir={(m) => onElegirMes(m.desde)}
        />
      </div>

      <Tabla etiqueta="Flujo del período por banco">
        <Cabecera>
          <Th>Banco</Th>
          <Th className="text-right">Ingresos</Th>
          <Th className="text-right">Egresos</Th>
          <Th className="text-right">Neto</Th>
        </Cabecera>
        <Cuerpo>
          {f.por_banco.length === 0 && (
            <TablaVacia colSpan={4}>Sin movimientos en el período.</TablaVacia>
          )}
          {f.por_banco.map((b) => (
            <Fila key={b.nombre}>
              <Td>{b.nombre}</Td>
              <Td className="text-right tabular-nums">{monto(b.ingresos)}</Td>
              <Td className="text-right tabular-nums">{monto(b.egresos)}</Td>
              <Td className="text-right tabular-nums">{monto(b.neto)}</Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
      {f.sin_convertir.length > 0 && (
        <p className="text-xs" style={{ color: "var(--warning)" }}>
          Sin convertir a dólares (no suman): {f.sin_convertir.join(", ")}.
        </p>
      )}
    </div>
  )
}
